import React, { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '../icons';

/**
 * Select — a styled dropdown that mirrors the design language of Input.js.
 *
 * Props:
 *   options      — array of { value, label, hint? }
 *                  value: any (null is a valid selection)
 *                  label: string displayed in the list and trigger
 *                  hint:  optional calm note shown below trigger when this option is selected
 *   value        — currently selected value (strict equality match)
 *   onChange     — fn(value) called when user picks an option; also clears error
 *   label        — optional field label rendered above the trigger
 *   placeholder  — text shown when no option matches value
 *   disabled     — greys out and blocks interaction
 *   error        — when set, replaces the option hint with a red error message
 *   className    — extra classes on the root wrapper
 */
const Select = ({
  options = [],
  value,
  onChange,
  label,
  placeholder = 'Select…',
  disabled = false,
  error = null,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const selectedOption = options.find(o => o.value === value) ?? null;

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (option) => {
    onChange(option.value);
    setIsOpen(false);
  };

  // Note shown below trigger: error takes priority over the option's calm hint
  const note = error || (!isOpen && selectedOption?.hint) || null;
  const noteIsError = !!error;

  const toggle = () => {
    if (!disabled) setIsOpen(o => !o);
  };

  return (
    <div className={`relative w-full ${className}`} ref={containerRef}>
      {/* Label */}
      {label && (
        <label className="block text-xs font-medium text-[#7B7B7B] dark:text-[#848484] mb-1.5">
          {label}
        </label>
      )}

      {/* Trigger */}
      <button
        type="button"
        onClick={toggle}
        disabled={disabled}
        className={`
          relative w-full flex items-center justify-between
          px-3 py-2 h-10 rounded-xl border
          text-sm font-medium text-left
          transition-colors
          border-[#ECECEC] dark:border-[#202020]
          ${disabled
            ? 'bg-[#F7F7F7] dark:bg-[#252525] cursor-not-allowed text-[#7B7B7B] dark:text-[#848484]'
            : 'bg-white dark:bg-[#1A1A1A] text-[#121212] dark:text-[#D0D0D0] hover:bg-[#F7F7F7] dark:hover:bg-[#252525] cursor-pointer'
          }
        `}
      >
        <span className={selectedOption ? '' : 'text-[#7B7B7B] dark:text-[#848484]'}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        {isOpen
          ? <ChevronUpIcon size={12} className="text-[#7B7B7B] dark:text-[#848484] flex-shrink-0 ml-2" />
          : <ChevronDownIcon size={12} className="text-[#7B7B7B] dark:text-[#848484] flex-shrink-0 ml-2" />
        }
      </button>

      {/* Note below trigger: calm hint or error — one slot, one message */}
      {note && (
        <p className={`mt-1.5 text-xs ${noteIsError ? 'text-red-500 dark:text-red-400' : 'text-[#7B7B7B] dark:text-[#848484]'}`}>
          {note}
        </p>
      )}

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-[#FCFCFC] dark:bg-[#1A1A1A] border border-[#ECECEC] dark:border-[#202020] rounded-xl shadow-lg overflow-hidden"
          style={{ boxShadow: '0 10px 21px -2px rgba(0,0,0,0.08)' }}
        >
          <div className="p-1">
            {options.map(option => {
              const isSelected = option.value === value;
              return (
                <button
                  key={String(option.value)}
                  type="button"
                  onClick={() => handleSelect(option)}
                  className={`
                    w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium text-left
                    transition-colors
                    ${isSelected
                      ? 'bg-[#F1F1F1] dark:bg-[#252525] text-[#121212] dark:text-[#D0D0D0]'
                      : 'text-[#121212] dark:text-[#D0D0D0] hover:bg-[#F7F7F7] dark:hover:bg-[#252525]'
                    }
                  `}
                >
                  <span>{option.label}</span>
                  {option.hint && (
                    <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded-md bg-[#F1F1F1] dark:bg-[#2A2A2A] text-[#7B7B7B] dark:text-[#848484] flex-shrink-0">
                      Pro
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Select;
